


































































































































import {
  computed,
  ref,
  defineComponent,
  getCurrentInstance,
  ComponentInstance,
  reactive,
  watch,
  toRefs,
  PropType,
} from '@vue/composition-api';
import {
  LockOnIcon,
  FileCopyIcon,
  LoadingIcon,
  HelpCircleIcon,
  CheckCircleFilledIcon,
  ErrorCircleFilledIcon,
} from 'tdesign-icons-vue';
import { InputValue, InputValueChangeContext } from 'tdesign-vue';
import _ from 'lodash';
import { encode } from 'js-base64';

import RemoveConfigConfirm from '@/renderer/views/desktop/ManageConsole/AccountAssociation/components/RemoveConfigConfirm.vue';

import { OauthProtocol, OidcProtocol } from '@/renderer/scripts/libs/smh-sdk-class/typings';
import { httpUrl } from '@/renderer/scripts/http';
import { copyText2Clipboard } from '@/renderer/scripts/utils/tools';
import tips from '@/renderer/scripts/utils/tips';
import { i18n_t } from '@/locales/i18n';

export default defineComponent({
  name: 'OauthorOIDCAssociation',
  components: {
    LockOnIcon,
    FileCopyIcon,
    LoadingIcon,
    HelpCircleIcon,
    CheckCircleFilledIcon,
    ErrorCircleFilledIcon,
    RemoveConfigConfirm,
  },
  props: {
    show: {
      required: true,
      type: Boolean,
      default: false,
    },
    protocolName: {
      type: String,
      default: 'oauth',
    },
    initData: {
      requred: true,
      type: Object as PropType<OauthProtocol | OidcProtocol>,
      default: () => ({}),
    },
    testResult: {
      type: String,
      required: true,
      default: '',
    },
  },
  setup(props, context) {
    const vm = getCurrentInstance()?.proxy || context.root;

    const innerShow = computed({
      get() {
        return props.show;
      },
      set(val) {
        context.emit('update:show', val);
      },
    });

    const oauthorOidcForm = ref<ComponentInstance | null>(null);

    const { testResult: innerTestResult, protocolName: innerProtocolName } = toRefs(props);

    const scopeRangeValue = ref('');

    const formData = reactive({
      redirectUri: '',
      clientId: '',
      clientSecret: '',
      ssoEndpoint: '',
      tokenEndpoint: '',
      userInfoEndpoint: '',
      loginFlag: '',
      jwksUri: '',
      jwtSecret: '',
      scopeRange: [],
      isCreateAccountOnLogin: false,
    });

    const isCreate = computed(() => !props.initData.ssoEndpoint);
    const isChangeClientSecret = ref(false);
    const isChangeJwtSecret = ref(false);
    const isValidateConfig = ref(false);
    const isValidateRequired = computed(() => {
      const isGeneralRequired =
        formData.clientId &&
        formData.clientSecret &&
        formData.ssoEndpoint &&
        formData.tokenEndpoint &&
        formData.loginFlag;
      return innerProtocolName.value === 'oauth'
        ? !!isGeneralRequired && (formData as OauthProtocol).userInfoEndpoint
        : !!isGeneralRequired;
    });
    const saveDisabled = computed(
      () => !isValidateRequired.value || !isValidateConfig.value || innerTestResult.value === 'testing',
    );

    const generalRules = {
      clientId: [
        {
          required: true,
          message: i18n_t('ClientId必填'),
          type: 'error',
        },
        {
          pattern: /^[^\u4e00-\u9fa5]*$/,
          message: i18n_t('ClientId不能含有中文'),
          type: 'error',
        },
      ],

      clientSecret: [
        {
          required: true,
          message: i18n_t('ClientSecret必填'),
          type: 'error',
        },
        {
          pattern: /^[^\u4e00-\u9fa5]*$/,
          message: i18n_t('ClientSecret不能含有中文'),
          type: 'error',
        },
      ],

      ssoEndpoint: [
        {
          required: true,
          message: i18n_t('授权登录地址必填'),
          type: 'error',
        },
        {
          url: {
            protocols: ['http', 'https'],
            require_protocol: 'true',
            message: i18n_t('仅支持url格式'),
            type: 'error',
          },
        },
      ],

      tokenEndpoint: [
        {
          required: true,
          message: i18n_t('Token地址必填'),
          type: 'error',
        },
        {
          url: {
            protocols: ['http', 'https'],
            require_protocol: 'true',
            message: i18n_t('仅支持url格式'),
            type: 'error',
          },
        },
      ],

      loginFlag: [
        {
          required: true,
          message: i18n_t('登录名字段必填'),
          type: 'error',
        },
      ],
    };
    const oidcRules = {
      ...generalRules,
      jwksUri: [
        {
          url: {
            protocols: ['http', 'https'],
            require_protocol: 'true',
            type: 'error',
          },
          message: i18n_t('仅支持url格式'),
        },
      ],
    };
    const oauthRules = {
      ...generalRules,
      userInfoEndpoint: [
        {
          required: true,
          message: i18n_t('OAuth Ticket校验地址必填'),
          type: 'error',
        },
        {
          url: {
            protocols: ['http', 'https'],
            require_protocol: 'true',
            message: i18n_t('仅支持url格式'),
            type: 'error',
          },
        },
      ],
    };
    const rules = computed(() => (innerProtocolName.value === 'oauth' ? oauthRules : oidcRules));

    const methods = {
      init() {
        Object.assign(formData, props.initData);
        isChangeClientSecret.value = false;
        isChangeJwtSecret.value = false;
        if (_.isEmpty(props.initData.loginFlag) && innerProtocolName.value === 'oidc') {
          // 默认oidc初始化登录名字段为sub
          formData.loginFlag = 'sub';
        }
        if (_.isEmpty(props.initData.redirectUri)) {
          formData.redirectUri = `${httpUrl.webUrl}/login`;
        } else {
          isValidateConfig.value = true;
        }
        setTimeout(() => {
          methods.clearRulesValidate();
        }, 0);
      },
      async copyText(text: string) {
        try {
          await copyText2Clipboard(text);
          tips.success(i18n_t('复制成功'));
        } catch (e: any) {
          tips.error(e);
        }
      },
      clearRulesValidate() {
        oauthorOidcForm.value && (oauthorOidcForm.value as any).clearValidate();
      },
      addScopeRange(val: InputValue, context?: InputValueChangeContext) {
        // 实现支持输入';'|'；'自动回车
        if (val.includes(';') || val.includes('；')) {
          const index = val.indexOf(';') || val.indexOf('；');
          (formData as OauthProtocol).scopeRange?.push(val.slice(0, index));
          scopeRangeValue.value = '';
        }
      },
      changeSecret(type: string) {
        if (type === 'client' && !isChangeClientSecret.value) {
          formData.clientSecret = '';
          isChangeClientSecret.value = true;
        } else if (type === 'jwt' && !isChangeJwtSecret.value) {
          formData.jwtSecret = '';
          isChangeJwtSecret.value = true;
        }
      },
      rebackSecret(type: string) {
        if (type === 'client' && formData.clientSecret === '') {
          formData.clientSecret = props.initData.clientSecret;
          isChangeClientSecret.value = false;
        } else if (type === 'jwt' && formData.jwtSecret === '') {
          formData.jwtSecret = (props.initData as OidcProtocol).jwtSecret as string;
          isChangeJwtSecret.value = false;
        }
      },
      getSubmitFormData() {
        let newFormData = {};
        if (innerProtocolName.value === 'oauth') {
          newFormData = _.omit(formData, ['jwksUri', 'jwtSecret']);
        } else {
          const tmpFormData = _.omit(formData, ['userInfoEndpoint', 'scopeRange']);
          newFormData = {
            ...tmpFormData,
            jwtSecret: isChangeJwtSecret.value ? encode(formData.jwtSecret) : formData.jwtSecret,
          };
        }
        return {
          ...newFormData,
          clientSecret: isChangeClientSecret.value ? encode(formData.clientSecret) : formData.clientSecret,
        };
      },
      async testConfig() {
        const result = await (oauthorOidcForm.value as any)?.validate();
        if (result === true) {
          context.emit('testConfig', { protocol: innerProtocolName.value, config: methods.getSubmitFormData() });
        }
      },
      async saveConfig() {
        const result = await (oauthorOidcForm.value as any)?.validate();
        if (result === true) {
          context.emit('saveConfig', { protocol: innerProtocolName.value, config: methods.getSubmitFormData() });
        }
      },
      confirmRemove() {
        methods.onClose();
        context.emit('removeProtocol', innerProtocolName.value);
      },
      throttleValidateConfigForm: _.throttle(async () => {
        // 如果表单form内容有改动 需重新将testResult置为空
        context.emit('update:testResult', '');
        if (isValidateRequired.value) {
          const result = await (oauthorOidcForm.value as any)?.validate();
          isValidateConfig.value = result === true;
        }
      }, 500),
      onClose() {
        innerShow.value = false;
        methods.clearRulesValidate();
      },
    };

    watch(
      () => innerShow.value,
      val => {
        if (val) {
          methods.init();
        }
      },
    );

    return {
      innerShow,
      oauthorOidcForm,
      innerTestResult,
      scopeRangeValue,
      innerProtocolName,
      isCreate,
      isChangeClientSecret,
      isChangeJwtSecret,
      isValidateConfig,
      isValidateRequired,
      saveDisabled,

      formData,
      rules,
      ...methods,
    };
  },
});
